import { useQuery } from '@tanstack/react-query'
import { useSupabase } from '@/lib/supabase-context'

export const balanceForwardKeys = {
  all: ['balance-forward'] as const,
  porCliente: (clienteId: string) => ['balance-forward', clienteId] as const,
}

/**
 * Suma el saldo que quedó impago de ANTES del período: pendiente de cada deuda menos
 * el excedente sin imputar de cada pago.
 *
 * Es deliberadamente la misma base que usa el acumulado del PDF en EstadoCuentaDialog
 * (`saldoAcum += pendiente - haberNeto`). Antes esto se calculaba en BRUTO
 * (`Σdebe - Σhaber`) y las dos bases no coincidían: un pago posterior al corte que
 * cancela una deuda anterior al corte aporta `haberNeto = 0` al acumulado, así que esa
 * deuda vieja nunca se descontaba del saldo anterior y terminaba contada dos veces
 * (ORR-224). Caso medido en PROD: el PDF daba 80.000 donde correspondía 40.000.
 *
 * Un resultado negativo significa saldo a favor del cliente; el PDF lo rotula así.
 */
export const sumarSaldoAnterior = (
  deudas: Array<{ saldo_pendiente: number | null }>,
  pagos: Array<{ saldo_a_favor: number | null }>,
): number =>
  deudas.reduce((acc, d) => acc + (d.saldo_pendiente ?? 0), 0) -
  pagos.reduce((acc, p) => acc + (p.saldo_a_favor ?? 0), 0)

export function useBalanceForward(clienteId: string, fechaDesde?: string) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: [...balanceForwardKeys.porCliente(clienteId), fechaDesde],
    queryFn: async () => {
      if (!fechaDesde) return null

      // Independientes entre sí: van en paralelo. Las vistas exponen cliente_id y fecha,
      // así que no hace falta resolver ids previos ni chunkear un .in() (ORR-171).
      const [deudas, pagos] = await Promise.all([
        supabase!
          .from('vista_saldos_deudas')
          .select('saldo_pendiente')
          .eq('cliente_id', clienteId)
          .lt('fecha', fechaDesde),
        supabase!
          .from('vista_saldos_pagos')
          .select('saldo_a_favor')
          .eq('cliente_id', clienteId)
          .lt('fecha', fechaDesde),
      ])

      if (deudas.error) throw deudas.error
      if (pagos.error) throw pagos.error

      return {
        saldo: sumarSaldoAnterior(deudas.data ?? [], pagos.data ?? []),
        fecha: fechaDesde,
      }
    },
    enabled: !!supabase && !supabaseLoading && !!clienteId && !!fechaDesde,
  })
}
